import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Divider,
  Alert,
  LinearProgress,
  Tooltip,
  Badge,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,

  Menu,
  ListItemButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  History as HistoryIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Send as SubmitIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Description as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as GenericFileIcon,
  CloudUpload as CloudUploadIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as ProjectIcon,
  Notifications as NotificationIcon,
  Comment as CommentIcon,
  Assessment as ReviewIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Group as StakeholdersIcon,
  Timeline as TimelineIcon,
  Forward as DelegateIcon,
  TrendingUp as EscalateIcon,
  PlayArrow as AdvanceIcon,
  Pause as HoldIcon,
  Stop as WithdrawIcon,
  AccessTime as OverdueIcon,
  Priority as PriorityIcon
} from '@mui/icons-material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DesignApprovals = () => {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create'); // create, edit, view, review, delegate, escalate
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [delegateUser, setDelegateUser] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inReview: 0,
    approved: 0,
    rejected: 0,
    overdue: 0
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    drawingId: '',
    revisionId: '',
    approvalType: 'design_review',
    priority: 'medium',
    requiredApprovers: [],
    dueDate: '',
    submissionNotes: '',
    files: [],
    approvalCriteria: [],
    dependencies: []
  });

  const priorities = ['low', 'medium', 'high', 'critical'];
  const approvalTypes = [
    'design_review',
    'code_compliance',
    'structural_review',
    'mep_review',
    'client_approval',
    'authority_approval',
    'final_approval'
  ];
  const statuses = [
    'draft',
    'submitted',
    'in_review',
    'pending_info',
    'approved',
    'approved_with_conditions',
    'rejected',
    'withdrawn',
    'on_hold'
  ];

  useEffect(() => {
    fetchApprovals();
    fetchStats();
    fetchProjects();
    fetchUsers();
  }, [searchTerm, statusFilter, priorityFilter, projectFilter]);

  const fetchApprovals = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      if (projectFilter !== 'all') params.append('projectId', projectFilter);
      
      const response = await api.get(`/design-approvals?${params.toString()}`);
      setApprovals(response.data);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/design-approvals/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      const data = response?.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.projects)
          ? data.projects
          : Array.isArray(data?.data)
            ? data.data
            : [];
      setProjects(list);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Drawings state for project-specific selection
  const [projectDrawings, setProjectDrawings] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(false);

  // Fetch approved drawings when project changes
  useEffect(() => {
    const loadDrawings = async () => {
      if (!formData.projectId) {
        setProjectDrawings([]);
        return;
      }
      try {
        setLoadingDrawings(true);
        const resp = await api.get(`/drawings?projectId=${formData.projectId}&status=approved`);
        const data = resp?.data;
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.drawings)
            ? data.drawings
            : Array.isArray(data?.data)
              ? data.data
              : [];
        setProjectDrawings(list);
      } catch (err) {
        console.error('Error fetching drawings for project:', err);
        setProjectDrawings([]);
      } finally {
        setLoadingDrawings(false);
      }
    };
    loadDrawings();
    // Reset selected drawing when project changes
    setFormData((prev) => ({ ...prev, drawingId: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectId]);

  const handleOpenDialog = (mode, approval = null) => {
    setDialogMode(mode);
    setSelectedApproval(approval);
    if (approval) {
      setFormData({
        title: approval.title || '',
        description: approval.description || '',
        projectId: (approval.projectId && approval.projectId._id) ? approval.projectId._id : (approval.projectId || ''),
        drawingId: Array.isArray(approval.relatedItems) 
          ? (approval.relatedItems.find(ri => ri.itemType === 'Drawing')?.itemId || '')
          : '',
        revisionId: approval.revisionId || '',
        approvalType: approval.approvalType || 'design_review',
        priority: approval.priority || 'medium',
        requiredApprovers: approval.requiredApprovers || [],
        dueDate: approval.deadline ? approval.deadline.split('T')[0] : '',
        submissionNotes: approval.urgencyReason || approval.submissionNotes || '',
        files: approval.attachments || [],
        approvalCriteria: approval.approvalCriteria || [],
        dependencies: approval.dependencies || []
      });
    } else {
      setFormData({
        title: '',
        description: '',
        projectId: '',
        drawingId: '',
        revisionId: '',
        approvalType: 'design_review',
        priority: 'medium',
        requiredApprovers: [],
        dueDate: '',
        submissionNotes: '',
        files: [],
        approvalCriteria: [],
        dependencies: []
      });
    }
    setSelectedFiles([]);
    setReviewComment('');
    setDelegateUser('');
    setEscalationReason('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedApproval(null);
    setSelectedFiles([]);
    setUploadProgress(0);
    setReviewComment('');
    setDelegateUser('');
    setEscalationReason('');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleSubmit = async () => {
    try {
      const submitData = new FormData();

      // Map to backend field names
      submitData.append('title', formData.title || '');
      submitData.append('description', formData.description || '');
      if (formData.projectId) submitData.append('projectId', formData.projectId);
      submitData.append('approvalType', formData.approvalType || 'design_review');
      submitData.append('priority', formData.priority || 'medium');
      if (formData.dueDate) submitData.append('deadline', formData.dueDate);
      if (formData.submissionNotes) submitData.append('urgencyReason', formData.submissionNotes);

      // Drawings selection (backend expects `drawings` as JSON array string or array under 'drawings')
      if (formData.drawingId) {
        submitData.append('drawings', JSON.stringify([formData.drawingId]));
      }

      // Optional arrays: send as single JSON string
      if (Array.isArray(formData.approvalCriteria)) {
        submitData.append('reviewCriteria', JSON.stringify(formData.approvalCriteria));
      }

      // Add files under 'attachments' as expected by backend
      selectedFiles.forEach(file => {
        submitData.append('attachments', file);
      });

      let response;
      if (dialogMode === 'create') {
        response = await api.post('/design-approvals', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      } else {
        response = await api.put(`/design-approvals/${selectedApproval._id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }

      await fetchApprovals();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving approval:', error);
    }
  };

  const handleSubmitForApproval = async (approvalId) => {
    try {
      await api.post(`/design-approvals/${approvalId}/submit`);
      await fetchApprovals();
      await fetchStats();
    } catch (error) {
      console.error('Error submitting approval:', error);
    }
  };

  const handleAdvanceStage = async (approvalId) => {
    try {
      await api.post(`/design-approvals/${approvalId}/advance`);
      await fetchApprovals();
      await fetchStats();
    } catch (error) {
      console.error('Error advancing stage:', error);
    }
  };

  const handleApprove = async (approvalId, conditions = '') => {
    try {
      await api.post(`/design-approvals/${approvalId}/approve`, {
        comment: reviewComment,
        conditions
      });
      await fetchApprovals();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error approving:', error);
    }
  };

  const handleReject = async (approvalId) => {
    try {
      await api.post(`/design-approvals/${approvalId}/reject`, {
        comment: reviewComment
      });
      await fetchApprovals();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error rejecting:', error);
    }
  };

  const handleDelegate = async (approvalId) => {
    try {
      await api.post(`/design-approvals/${approvalId}/delegate`, {
        delegateTo: delegateUser,
        comment: reviewComment
      });
      await fetchApprovals();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error delegating:', error);
    }
  };

  const handleEscalate = async (approvalId) => {
    try {
      await api.post(`/design-approvals/${approvalId}/escalate`, {
        reason: escalationReason,
        comment: reviewComment
      });
      await fetchApprovals();
      await fetchStats();
      handleCloseDialog();
    } catch (error) {
      console.error('Error escalating:', error);
    }
  };

  const handleDelete = async (approvalId) => {
    if (window.confirm('Are you sure you want to delete this approval?')) {
      try {
        await api.delete(`/design-approvals/${approvalId}`);
        await fetchApprovals();
        await fetchStats();
      } catch (error) {
        console.error('Error deleting approval:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      submitted: 'info',
      in_review: 'warning',
      pending_info: 'warning',
      approved: 'success',
      approved_with_conditions: 'success',
      rejected: 'error',
      withdrawn: 'secondary',
      on_hold: 'secondary'
    };
    return colors[status] || 'default';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'success',
      medium: 'warning',
      high: 'error',
      critical: 'error'
    };
    return colors[priority] || 'default';
  };

  const getFileIcon = (filename) => {
    const extension = filename.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf': return <PdfIcon />;
      case 'jpg': case 'jpeg': case 'png': case 'gif': return <ImageIcon />;
      case 'dwg': case 'dxf': return <FileIcon />;
      default: return <GenericFileIcon />;
    }
  };

  const isOverdue = (dueDate, status) => {
    if (!dueDate || ['approved', 'rejected', 'withdrawn'].includes(status)) return false;
    return new Date(dueDate) < new Date();
  };

  const filteredApprovals = approvals.filter(approval => {
    const matchesSearch = !searchTerm || 
      approval.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      approval.approvalType?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || approval.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || approval.priority === priorityFilter;
    const matchesProject = projectFilter === 'all' || (approval.projectId?._id === projectFilter);
    
    return matchesSearch && matchesStatus && matchesPriority && matchesProject;
  });

  const renderApprovalDialog = () => {
    return (
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' && 'Create New Design Approval'}
          {dialogMode === 'edit' && 'Edit Design Approval'}
          {dialogMode === 'view' && 'Design Approval Details'}
          {dialogMode === 'review' && 'Review Design Approval'}
          {dialogMode === 'delegate' && 'Delegate Approval'}
          {dialogMode === 'escalate' && 'Escalate Approval'}
        </DialogTitle>
        <DialogContent>
          {dialogMode === 'review' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Review: {selectedApproval?.title}
              </Typography>
              <Typography variant="body1" paragraph>
                {selectedApproval?.description}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Priority:</Typography>
                  <Chip 
                    label={selectedApproval?.priority} 
                    color={getPriorityColor(selectedApproval?.priority)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2">Type:</Typography>
                  <Chip 
                    label={selectedApproval?.approvalType?.replace('_', ' ')} 
                    size="small"
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              
              <TextField
                fullWidth
                label="Review Comment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                multiline
                rows={4}
                sx={{ mb: 3 }}
                required
              />
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleApprove(selectedApproval._id)}
                  startIcon={<ApproveIcon />}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => handleApprove(selectedApproval._id, 'with conditions')}
                  startIcon={<ApproveIcon />}
                >
                  Approve with Conditions
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleReject(selectedApproval._id)}
                  startIcon={<RejectIcon />}
                >
                  Reject
                </Button>
              </Box>
            </Box>
          ) : dialogMode === 'delegate' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Delegate: {selectedApproval?.title}
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Delegate To</InputLabel>
                <Select
                  value={delegateUser}
                  onChange={(e) => setDelegateUser(e.target.value)}
                >
                  {users.map(user => (
                    <MenuItem key={user._id} value={user._id}>
                      {user.name} - {user.role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Delegation Comment"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  onClick={() => handleDelegate(selectedApproval._id)}
                  startIcon={<DelegateIcon />}
                  disabled={!delegateUser}
                >
                  Delegate
                </Button>
              </Box>
            </Box>
          ) : dialogMode === 'escalate' ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                Escalate: {selectedApproval?.title}
              </Typography>
              
              <TextField
                fullWidth
                label="Escalation Reason"
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
                required
              />
              
              <TextField
                fullWidth
                label="Additional Comments"
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => handleEscalate(selectedApproval._id)}
                  startIcon={<EscalateIcon />}
                  disabled={!escalationReason}
                >
                  Escalate
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
                <Tab label="Basic Info" />
                <Tab label="Approvers & Timeline" />
                <Tab label="Files" />
                <Tab label="Workflow" />
              </Tabs>
              
              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      autoFocus
                      label="Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      disabled={dialogMode === 'view'}
                      required
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={dialogMode === 'view'}
                      multiline
                      rows={4}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Project</InputLabel>
                      <Select
                        value={Array.isArray(projects) && projects.some(p => p._id === formData.projectId) ? formData.projectId : ''}
                        onChange={(e) => handleInputChange('projectId', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        <MenuItem value="">
                          <em>Select a project</em>
                        </MenuItem>
                        {Array.isArray(projects) && projects.map(project => (
                          <MenuItem key={project._id} value={project._id}>
                            {project.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Design (Approved)</InputLabel>
                      <Select
                        value={Array.isArray(projectDrawings) && projectDrawings.some(d => d._id === formData.drawingId) ? formData.drawingId : ''}
                        onChange={(e) => handleInputChange('drawingId', e.target.value)}
                        disabled={dialogMode === 'view' || !formData.projectId || loadingDrawings}
                      >
                        <MenuItem value="">
                          <em>{!formData.projectId ? 'Select a project first' : (loadingDrawings ? 'Loading approved designs…' : 'Select a design')}</em>
                        </MenuItem>
                        {projectDrawings.map(d => (
                          <MenuItem key={d._id} value={d._id}>
                            {d.drawingNumber ? `${d.drawingNumber} — ${d.title}` : d.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Approval Type</InputLabel>
                      <Select
                        value={formData.approvalType}
                        onChange={(e) => handleInputChange('approvalType', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {approvalTypes.map(type => (
                          <MenuItem key={type} value={type}>
                            {type.replace('_', ' ').charAt(0).toUpperCase() + type.replace('_', ' ').slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={formData.priority}
                        onChange={(e) => handleInputChange('priority', e.target.value)}
                        disabled={dialogMode === 'view'}
                      >
                        {priorities.map(priority => (
                          <MenuItem key={priority} value={priority}>
                            {priority.charAt(0).toUpperCase() + priority.slice(1)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Due Date"
                      value={formData.dueDate}
                      onChange={(e) => handleInputChange('dueDate', e.target.value)}
                      disabled={dialogMode === 'view'}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Submission Notes"
                      value={formData.submissionNotes}
                      onChange={(e) => handleInputChange('submissionNotes', e.target.value)}
                      disabled={dialogMode === 'view'}
                      multiline
                      rows={3}
                    />
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 1 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Required Approvers
                    </Typography>
                    <List>
                      {formData.requiredApprovers.map((approver, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Avatar sx={{ width: 32, height: 32 }}>
                              {approver.name?.charAt(0)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={approver.name}
                            secondary={approver.role}
                          />
                          <Chip 
                            label={approver.status || 'pending'} 
                            size="small" 
                            color={approver.status === 'approved' ? 'success' : approver.status === 'rejected' ? 'error' : 'default'}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Approval Criteria
                    </Typography>
                    <List>
                      {formData.approvalCriteria.map((criteria, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={criteria} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                </Grid>
              )}
              
              {tabValue === 2 && (
                <Box>
                  {dialogMode !== 'view' && (
                    <Box sx={{ mb: 3 }}>
                      <input
                        accept=".pdf,.dwg,.dxf,.jpg,.jpeg,.png,.doc,.docx"
                        style={{ display: 'none' }}
                        id="file-upload"
                        multiple
                        type="file"
                        onChange={handleFileSelect}
                      />
                      <label htmlFor="file-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                          fullWidth
                          sx={{ py: 2 }}
                        >
                          Select Files to Upload
                        </Button>
                      </label>
                      {selectedFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected Files:
                          </Typography>
                          <List dense>
                            {selectedFiles.map((file, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  {getFileIcon(file.name)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={file.name}
                                  secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      {uploadProgress > 0 && uploadProgress < 100 && (
                        <Box sx={{ mt: 2 }}>
                          <LinearProgress variant="determinate" value={uploadProgress} />
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            Uploading... {uploadProgress}%
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {formData.files.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Current Files:
                      </Typography>
                      <List>
                        {formData.files.map((file, index) => (
                          <ListItem key={index}>
                            <ListItemIcon>
                              {getFileIcon(file.filename)}
                            </ListItemIcon>
                            <ListItemText
                              primary={file.filename}
                              secondary={`${file.fileType} • ${(file.fileSize / 1024 / 1024).toFixed(2)} MB`}
                            />
                            <IconButton size="small">
                              <DownloadIcon />
                            </IconButton>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
              
              {tabValue === 3 && selectedApproval && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Approval Workflow
                  </Typography>
                  <Timeline>
                    {selectedApproval.workflowHistory?.map((step, index) => (
                      <TimelineItem key={index}>
                        <TimelineOppositeContent color="textSecondary">
                          {new Date(step.timestamp).toLocaleString()}
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot color={
                            step.action === 'approved' ? 'success' : 
                            step.action === 'rejected' ? 'error' : 
                            step.action === 'submitted' ? 'info' : 'primary'
                          }>
                            {step.action === 'approved' && <ApproveIcon />}
                            {step.action === 'rejected' && <RejectIcon />}
                            {step.action === 'submitted' && <SubmitIcon />}
                            {step.action === 'delegated' && <DelegateIcon />}
                            {step.action === 'escalated' && <EscalateIcon />}
                            {!['approved', 'rejected', 'submitted', 'delegated', 'escalated'].includes(step.action) && <AssignmentIcon />}
                          </TimelineDot>
                          {index < selectedApproval.workflowHistory.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <Typography variant="h6" component="span">
                            {step.action.charAt(0).toUpperCase() + step.action.slice(1)}
                          </Typography>
                          <Typography>{step.comment}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            By {step.user?.name}
                          </Typography>
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          {dialogMode !== 'view' && dialogMode !== 'review' && dialogMode !== 'delegate' && dialogMode !== 'escalate' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading design approvals...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Design Approvals
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage design approval workflows and processes
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('create')}
        >
          Create Approval
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total
                  </Typography>
                  <Typography variant="h5">
                    {stats.total}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <AssignmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending
                  </Typography>
                  <Typography variant="h5">
                    {stats.pending}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'grey.500' }}>
                  <ScheduleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    In Review
                  </Typography>
                  <Typography variant="h5">
                    {stats.inReview}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <ReviewIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Approved
                  </Typography>
                  <Typography variant="h5">
                    {stats.approved}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <ApproveIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Rejected
                  </Typography>
                  <Typography variant="h5">
                    {stats.rejected}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <RejectIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Overdue
                  </Typography>
                  <Typography variant="h5" color="error">
                    {stats.overdue}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'error.main' }}>
                  <OverdueIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search approvals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  {statuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  {priorities.map(priority => (
                    <MenuItem key={priority} value={priority}>
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Project</InputLabel>
                <Select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                >
                  <MenuItem value="all">All Projects</MenuItem>
                  {Array.isArray(projects) && projects.map(project => (
                    <MenuItem key={project._id} value={project._id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                  setPriorityFilter('all');
                  setProjectFilter('all');
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Approvals Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredApprovals.map((approval) => (
                  <TableRow key={approval._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {isOverdue(approval.dueDate, approval.status) && (
                          <Tooltip title="Overdue">
                            <OverdueIcon color="error" sx={{ mr: 1, fontSize: 16 }} />
                          </Tooltip>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {approval.title}
                          </Typography>
                          {approval.description && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {approval.description.substring(0, 60)}...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={approval.approvalType?.replace('_', ' ')} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={approval.priority} 
                        color={getPriorityColor(approval.priority)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={approval.status?.replace('_', ' ')} 
                        color={getStatusColor(approval.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {approval.projectId?.name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        color={isOverdue(approval.deadline, approval.status) ? 'error' : 'inherit'}
                      >
                        {approval.deadline ? new Date(approval.deadline).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={approval.progress || 0} 
                          sx={{ width: 60, height: 6 }}
                        />
                        <Typography variant="caption">
                          {approval.progress || 0}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('view', approval)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('edit', approval)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {approval.status === 'draft' && (
                          <Tooltip title="Submit">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleSubmitForApproval(approval._id)}
                            >
                              <SubmitIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {approval.status === 'in_review' && (
                          <Tooltip title="Review">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenDialog('review', approval)}
                            >
                              <CommentIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="More Actions">
                          <IconButton 
                            size="small" 
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                          >
                            <MoreIcon />
                          </IconButton>
                        </Tooltip>
                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl)}
                          onClose={() => setAnchorEl(null)}
                        >
                          <MenuItem onClick={() => {
                            handleOpenDialog('delegate', approval);
                            setAnchorEl(null);
                          }}>
                            <DelegateIcon sx={{ mr: 1 }} /> Delegate
                          </MenuItem>
                          <MenuItem onClick={() => {
                            handleOpenDialog('escalate', approval);
                            setAnchorEl(null);
                          }}>
                            <EscalateIcon sx={{ mr: 1 }} /> Escalate
                          </MenuItem>
                          <MenuItem onClick={() => {
                            handleAdvanceStage(approval._id);
                            setAnchorEl(null);
                          }}>
                            <AdvanceIcon sx={{ mr: 1 }} /> Advance Stage
                          </MenuItem>
                          <Divider />
                          <MenuItem 
                            onClick={() => {
                              handleDelete(approval._id);
                              setAnchorEl(null);
                            }}
                            sx={{ color: 'error.main' }}
                          >
                            <DeleteIcon sx={{ mr: 1 }} /> Delete
                          </MenuItem>
                        </Menu>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {filteredApprovals.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No design approvals found
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || projectFilter !== 'all'
                  ? 'Try adjusting your search criteria'
                  : 'Get started by creating your first design approval'
                }
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {renderApprovalDialog()}
    </Box>
  );
};

export default DesignApprovals;